import { useEffect, useMemo, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { FileText, Image, Paperclip, X } from 'lucide-react';

import {
  MessageDocumentCard,
  RichMessageContent,
} from '@/components/content/RichMessageContent';
import {
  useCreateThread,
  useMarkThreadRead,
  usePostMessage,
  usePostMessageAttachment,
  useThread,
} from '@/lib/api/hooks';
import { Icon as BrandIcon } from '@/lib/Icon';
import { cn } from '@/lib/cn';
import type { Message } from '@/types/api';
import { RouteNudge } from '@/features/messaging/components/RouteNudge';

import { messageDrawer, useMessageDrawerTarget } from '../stores/messageDrawer';

/**
 * MessageDrawer — slide-up drawer that hosts BOTH:
 *   - Composing a new clinician message (target === 'new')
 *   - Viewing / replying to an existing thread (target === '<threadId>')
 *
 * Mirrors the OverflowDrawer chrome: mobile slides up from below the
 * SearchBar (`top-45 bottom-0 inset-x-0`); desktop docks as a right
 * rail. Header reuses the shared drawer pattern (`bg-sunken` band,
 * `font-mono uppercase text-13` title, `bg-slate` X close on the right).
 *
 * Empty composer + existing-thread compose share one sticky bottom
 * input so the visual treatment stays consistent.
 */
export function MessageDrawer() {
  const target = useMessageDrawerTarget();
  const open = target !== null;
  const threadId = target && target !== 'new' ? target : null;

  return (
    <>
      {/* Scrim — tapping closes. Matches OverflowDrawer chrome. */}
      {open && (
        <button
          type="button"
          aria-label="Close message drawer"
          onClick={() => messageDrawer.close()}
          className="fixed inset-0 bg-black/10 backdrop-blur-[10px] z-helpdock"
        />
      )}
      <aside
        aria-hidden={!open}
        aria-label="Messages"
        className={cn(
          'fixed inset-x-0 top-45 bottom-0 md:top-0 md:right-0 md:left-auto md:w-[420px] z-drawer',
          'bg-[var(--surface-page)] border-l border-line-subtle flex flex-col',
          'transition-transform duration-base ease-ease',
          open
            ? 'translate-y-0 md:translate-x-0'
            : 'translate-y-full md:translate-y-0 md:translate-x-full pointer-events-none',
        )}
      >
        {/* Mount the body conditionally so each mode's local state
            resets when the drawer reopens for a different target. */}
        {target === 'new' && <NewMessageBody />}
        {threadId && <ThreadBody threadId={threadId} />}
        {/* Render an empty shell when fully closed so the slide-out
            animation has something to animate from. */}
        {!open && <ClosedShell />}
      </aside>
    </>
  );
}

// ---------- Shared chrome ----------

function DrawerHeader({ title }: { title: string }) {
  return (
    <header className="flex items-center justify-between bg-[var(--surface-sunken)] border-b border-line-subtle pr-2">
      <h2 className="font-mono uppercase text-13 text-ink-secondary px-15 py-15">
        {title}
      </h2>
      <button
        type="button"
        onClick={() => messageDrawer.close()}
        aria-label="Close"
        className="w-45 h-45 flex items-center justify-center border-l border-line-subtle bg-slate text-ink-secondary hover:text-ink"
      >
        <X size={18} strokeWidth={1.5} />
      </button>
    </header>
  );
}

function Composer({
  value,
  onChange,
  onSend,
  disabled,
  pending,
  attachment,
  onAttachment,
  onClearAttachment,
  placeholder = 'Type a message...',
}: {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  disabled: boolean;
  pending: boolean;
  attachment?: File | null;
  onAttachment?: (file: File) => void;
  onClearAttachment?: () => void;
  placeholder?: string;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Auto-resize the textarea up to a 30rem cap, iMessage-style. Reset
  // to auto first so shrinking after a delete also recalculates down.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSend();
      }}
      className="border-t border-line-subtle bg-[var(--surface-page)] p-15 flex flex-col gap-10"
    >
      {attachment && (
        <div className="flex items-center gap-8 px-10 py-8 border border-line-subtle bg-[var(--surface-sunken)] text-12">
          {attachment.type.startsWith('image/') ? (
            <Image size={15} />
          ) : (
            <FileText size={15} />
          )}
          <span className="truncate flex-1">{attachment.name}</span>
          <button
            type="button"
            onClick={onClearAttachment}
            aria-label="Remove attachment"
            className="p-2"
          >
            <X size={14} />
          </button>
        </div>
      )}
      <div className="flex items-end gap-10 w-full">
        {onAttachment && (
          <>
            <input
              ref={fileRef}
              type="file"
              className="sr-only"
              accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
              onChange={(event) => {
                const selected = event.target.files?.[0];
                if (selected) onAttachment(selected);
                event.target.value = '';
              }}
            />
            <button
              type="button"
              aria-label="Attach image or PDF"
              title="Attach image or PDF (10 MB max)"
              disabled={pending}
              onClick={() => fileRef.current?.click()}
              className="w-40 h-45 shrink-0 flex items-center justify-center border border-line-subtle text-ink-secondary hover:text-ink disabled:opacity-50"
            >
              <Paperclip size={17} />
            </button>
          </>
        )}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
          rows={1}
          placeholder={placeholder}
          className="flex-1 min-w-0 resize-none px-15 py-12 bg-[#FAFAFA] border border-[#ECECEC] text-black text-13 leading-150 placeholder:text-[#7C7C7C] focus:outline-none max-h-[25rem] overflow-y-auto"
        />
        <button
          type="submit"
          aria-label="Send"
          disabled={disabled || pending}
          className="w-45 h-45 shrink-0 flex items-center justify-center bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span aria-hidden className="inline-flex w-8">
            <BrandIcon name="Arrow" viewBox="0 0 10 12" />
          </span>
        </button>
      </div>
    </form>
  );
}

// ---------- Care-team messaging: AI misroute check ----------

/**
 * Optional pre-send check (care-team-messaging experiment). Resolves to a
 * topic when the message looks like an order/billing question that System
 * Support should take, or null to send to providers as normal.
 */
export type ProviderRouteCheck = (
  text: string,
) => Promise<{ topic: string | null } | null>;

export interface ProviderConversationProps {
  routeCheck?: ProviderRouteCheck;
  onRouteToSupport?: (text: string) => void;
  /** Lets the caller start the route check while the patient types. */
  onDraftChange?: (text: string) => void;
  placeholder?: string;
}

type PendingSend = { text: string; phase: 'checking' | 'held' | 'sending' };

/**
 * Care-team messaging send path: the message shows in the chat straight
 * away (faded, "Sending…") while the AI routing check runs. If the check
 * says it's an ops question it stays "Not sent" under the nudge until the
 * patient picks Support, Providers anyway, or edits it. Any check failure
 * sends to providers — the check is advisory.
 */
function useRoutedSend({
  routeCheck,
  onRouteToSupport,
  send,
  restore,
}: {
  routeCheck?: ProviderRouteCheck;
  onRouteToSupport?: (text: string) => void;
  send: (text: string) => Promise<unknown>;
  restore: (text: string) => void;
}) {
  const [pending, setPending] = useState<PendingSend | null>(null);
  const [topic, setTopic] = useState<string | null>(null);

  const deliver = async (text: string) => {
    setPending({ text, phase: 'sending' });
    try {
      await send(text);
      setPending(null);
    } catch {
      // The mutation's own error renders below the thread; hand the text back.
      setPending(null);
      restore(text);
    }
  };

  const submit = async (text: string) => {
    setPending({ text, phase: 'checking' });
    let verdict: Awaited<ReturnType<ProviderRouteCheck>> = null;
    try {
      verdict = routeCheck ? await routeCheck(text) : null;
    } catch {
      verdict = null;
    }
    if (verdict) {
      setTopic(verdict.topic);
      setPending({ text, phase: 'held' });
      return;
    }
    await deliver(text);
  };

  return {
    pending,
    topic,
    submit,
    toSupport: () => {
      if (!pending) return;
      onRouteToSupport?.(pending.text);
      setPending(null);
    },
    sendAnyway: () => {
      if (pending) void deliver(pending.text);
    },
    edit: () => {
      if (!pending) return;
      restore(pending.text);
      setPending(null);
    },
  };
}

function PendingBubble({ pending }: { pending: PendingSend }) {
  return (
    <div className="flex flex-col items-end gap-4" aria-live="polite">
      <div className="max-w-[85%] px-15 py-12 text-13 bg-[var(--surface-sunken)] text-ink opacity-60">
        {pending.text}
      </div>
      <span className="font-mono uppercase text-10 text-ink-muted">
        {pending.phase === 'held' ? 'Not sent' : 'Sending…'}
      </span>
    </div>
  );
}

// ---------- New-message mode ----------

function NewMessageBody() {
  return (
    <>
      <DrawerHeader title="New message" />
      {/* Switch the drawer into thread mode so the patient sees their
          message land in-place instead of bouncing to a new screen. */}
      <NewProviderConversation
        onCreated={(threadId) => messageDrawer.openThread(threadId)}
      />
    </>
  );
}

export function NewProviderConversation({
  onCreated,
  routeCheck,
  onRouteToSupport,
  onDraftChange,
  placeholder,
}: ProviderConversationProps & { onCreated?: (threadId: string) => void }) {
  const createThread = useCreateThread();
  const [body, setBody] = useState('');
  const trimmed = body.trim();
  const create = async (text: string) => {
    const thread = await createThread.mutateAsync({ subject: null, body: text });
    onCreated?.(thread.id);
  };
  const routed = useRoutedSend({ routeCheck, onRouteToSupport, send: create, restore: setBody });

  const handleSend = async () => {
    if (!trimmed || createThread.isPending || routed.pending) return;
    if (routeCheck) {
      setBody('');
      await routed.submit(trimmed);
      return;
    }
    await create(trimmed);
    setBody('');
  };

  return (
    <>
      {/* Empty body until the message is actually sent — the composer
          alone provides feedback while typing. */}
      <div className="flex-1 overflow-y-auto p-15 flex flex-col justify-end">
        {routed.pending && <PendingBubble pending={routed.pending} />}
      </div>

      {createThread.error && (
        <div role="alert" className="px-15 pb-10 text-13 text-danger">
          {(createThread.error as { message?: string }).message ??
            'Send failed'}
        </div>
      )}

      {routed.pending?.phase === 'held' && (
        <RouteNudge
          topic={routed.topic}
          onAskSupport={routed.toSupport}
          onSendAnyway={routed.sendAnyway}
          onEdit={routed.edit}
        />
      )}

      <Composer
        value={body}
        onChange={(next) => {
          setBody(next);
          onDraftChange?.(next);
        }}
        onSend={() => void handleSend()}
        disabled={!trimmed}
        pending={createThread.isPending || Boolean(routed.pending)}
        placeholder={placeholder}
      />
    </>
  );
}

// ---------- Existing-thread mode ----------

function ThreadBody({ threadId }: { threadId: string }) {
  const { data: thread } = useThread(threadId);
  const title = useMemo(() => {
    if (!thread) return 'Conversation';
    const name = thread.participants[0]?.display_name;
    return (name || thread.subject || 'Conversation').toUpperCase();
  }, [thread]);

  return (
    <>
      <DrawerHeader title={title} />
      <ProviderThreadConversation threadId={threadId} />
    </>
  );
}

export function ProviderThreadConversation({
  threadId,
  routeCheck,
  onRouteToSupport,
  onDraftChange,
  placeholder,
}: ProviderConversationProps & { threadId: string }) {
  const { data: thread, isLoading, error } = useThread(threadId);
  const postMessage = usePostMessage(threadId);
  const postAttachment = usePostMessageAttachment(threadId);
  const markRead = useMarkThreadRead();
  const [body, setBody] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const trimmed = body.trim();
  const routed = useRoutedSend({
    routeCheck,
    onRouteToSupport,
    send: (text) => postMessage.mutateAsync({ body: text }),
    restore: setBody,
  });

  // Scroll to bottom on new messages (and when a pending send appears).
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [thread?.messages.length, routed.pending?.phase]);

  // Mark read when opened + when new messages arrive while visible.
  useEffect(() => {
    if (!threadId) return;
    markRead.mutate(threadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, thread?.messages.length]);

  const handleSend = async () => {
    if (
      (!trimmed && !attachment) ||
      postMessage.isPending ||
      postAttachment.isPending ||
      routed.pending
    )
      return;
    // Attachments (photos of a reaction, lab PDFs) always go to providers.
    if (routeCheck && !attachment) {
      setBody('');
      await routed.submit(trimmed);
      return;
    }
    if (attachment) {
      await postAttachment.mutateAsync({ file: attachment, body: trimmed });
    } else {
      await postMessage.mutateAsync({ body: trimmed });
    }
    setBody('');
    setAttachment(null);
  };

  return (
    <>
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-15 flex flex-col gap-15"
      >
        {isLoading && (
          <div className="m-auto text-13 text-ink-muted">Loading…</div>
        )}
        {error && (
          <div className="m-auto text-13 text-danger">
            Couldn’t load this thread.
          </div>
        )}
        {thread && thread.messages.length === 0 && (
          <div className="m-auto text-13 text-ink-muted">
            No messages yet — start the conversation.
          </div>
        )}
        {thread &&
          thread.messages.map((m, i) => {
            const prev = thread.messages[i - 1];
            const showTimestamp =
              !prev ||
              Math.abs(
                parseISO(m.sent_at).getTime() -
                  parseISO(prev.sent_at).getTime(),
              ) >
                5 * 60_000;
            // Re-introduce the sender header whenever the author changes
            // (or after a timestamp break) so multi-sender threads — doctor
            // vs support team — stay attributable at a glance.
            const showSender =
              m.sender_role !== 'patient' &&
              (showTimestamp || !prev || prev.sender_name !== m.sender_name);
            return (
              <div key={m.id} className="flex flex-col gap-10">
                {showTimestamp && (
                  <div className="self-center text-13 text-[#7C7C7C]">
                    {format(parseISO(m.sent_at), 'h:mma')}
                  </div>
                )}
                <MessageBubble message={m} showSender={showSender} />
              </div>
            );
          })}
        {routed.pending && <PendingBubble pending={routed.pending} />}
      </div>

      {(postMessage.error || postAttachment.error) && (
        <div role="alert" className="px-15 pb-10 text-13 text-danger">
          {((postMessage.error || postAttachment.error) as { message?: string })
            .message ?? 'Send failed'}
        </div>
      )}

      {routed.pending?.phase === 'held' && (
        <RouteNudge
          topic={routed.topic}
          onAskSupport={routed.toSupport}
          onSendAnyway={routed.sendAnyway}
          onEdit={routed.edit}
        />
      )}

      <Composer
        value={body}
        onChange={(next) => {
          setBody(next);
          onDraftChange?.(next);
        }}
        onSend={() => void handleSend()}
        disabled={!trimmed && !attachment}
        pending={postMessage.isPending || postAttachment.isPending || Boolean(routed.pending)}
        attachment={attachment}
        onAttachment={setAttachment}
        onClearAttachment={() => setAttachment(null)}
        placeholder={placeholder}
      />
    </>
  );
}

function senderInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function MessageBubble({
  message,
  showSender = false,
}: {
  message: Message;
  showSender?: boolean;
}) {
  const isPatient = message.sender_role === 'patient';
  const [avatarFailed, setAvatarFailed] = useState(false);
  if (isPatient) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] px-15 py-12 text-13 bg-[var(--surface-sunken)] text-ink flex flex-col gap-8">
          {message.body && <RichMessageContent text={message.body} />}
          <MessageAttachments attachments={message.attachments ?? []} />
        </div>
      </div>
    );
  }
  // Care-team side: a tiny avatar inline with the sender's NAME (not a
  // gutter beside the bubble — bubbles stay full width) so the patient can
  // tell the support team apart from their doctor in the same thread.
  return (
    <div className="flex flex-col gap-4 items-start">
      {showSender && (
        <div className="flex items-center gap-6 px-2">
          {message.sender_avatar_url && !avatarFailed ? (
            <img
              src={message.sender_avatar_url}
              alt=""
              className="w-16 h-16 rounded-full object-cover shrink-0"
              onError={() => setAvatarFailed(true)}
            />
          ) : (
            <span
              aria-hidden
              className="w-16 h-16 rounded-full shrink-0 flex items-center justify-center bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)] text-[8px] font-mono"
            >
              {senderInitials(message.sender_name)}
            </span>
          )}
          <span className="text-12 text-[#7C7C7C]">{message.sender_name}</span>
        </div>
      )}
      <div className="max-w-[85%] px-15 py-12 text-13 bg-[var(--surface-sunken)] text-ink flex flex-col gap-8">
        {message.body && <RichMessageContent text={message.body} />}
        <MessageAttachments attachments={message.attachments ?? []} />
      </div>
    </div>
  );
}

function MessageAttachments({
  attachments,
}: {
  attachments: Message['attachments'];
}) {
  if (!attachments.length) return null;
  return (
    <div className="flex flex-col gap-8">
      {attachments.map((attachment, index) => {
        const key = `${attachment.url}-${index}`;
        if (attachment.kind === 'image') {
          return (
            <a
              key={key}
              href={attachment.url}
              target="_blank"
              rel="noreferrer"
              className="block"
            >
              <img
                src={attachment.url}
                alt={attachment.name || 'Chat attachment'}
                loading="lazy"
                className="block max-w-full max-h-80 object-contain border border-line-subtle bg-white"
              />
            </a>
          );
        }
        if (attachment.kind === 'audio') {
          return (
            <audio
              key={key}
              src={attachment.url}
              controls
              className="max-w-full"
            />
          );
        }
        return (
          <MessageDocumentCard
            key={key}
            href={attachment.url}
            title={attachment.name || 'View document'}
            mime={attachment.mime}
          />
        );
      })}
    </div>
  );
}

// Tiny placeholder while drawer is fully closed — keeps the aside's
// flex children consistent so the slide animation doesn't jump.
function ClosedShell() {
  return <div className="flex-1" />;
}
